// Client side of the OAuth round-trip (login and "link a provider").
//
// We generate `state` and a PKCE verifier, keep them in sessionStorage, and
// send the browser to the API. On return (/auth/callback) we require the
// returned `state` to equal the stored one — that comparison is what stops
// login CSRF — and prove possession of the verifier when exchanging the code.
// Inside Electron the round-trip runs in the system browser and comes back
// through the `sss://auth/callback` deep link (see apps/desktop).

import type { LoginResponse, OAuthPlatform, OAuthProvider } from '@sss/sdk';
import { sdk } from './sdk';
import { API_BASE_URL, isElectron } from './config';
import { pkceChallenge, randomUrlSafe } from './pkce';

const STATE_KEY = 'sss.oauth.state';
const VERIFIER_KEY = 'sss.oauth.verifier';

const platform: OAuthPlatform = isElectron ? 'desktop' : 'web';

const openAuthUrl = (url: string): void => {
  if (isElectron) window.electronAPI!.openExternal(url);
  else window.location.assign(url);
};

/** Begin "Continue with <provider>". `returnUrl` must be a same-site path. */
export async function startOAuthLogin(provider: OAuthProvider, returnUrl?: string): Promise<void> {
  const state = randomUrlSafe(24);
  const verifier = randomUrlSafe(32);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  openAuthUrl(sdk.oauth.authorizeUrl({ provider, state, platform, returnUrl, codeChallenge: await pkceChallenge(verifier) }));
}

/** Begin linking `provider` to the signed-in account. */
export async function startOAuthLink(provider: OAuthProvider): Promise<void> {
  const verifier = randomUrlSafe(32);
  const intent = await sdk.oauth.linkIntent(provider, platform, await pkceChallenge(verifier));
  sessionStorage.setItem(STATE_KEY, intent.state);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  openAuthUrl(`${API_BASE_URL}${intent.url}`);
}

export type OAuthCallbackResult =
  | { kind: 'login'; result: LoginResponse & { returnUrl?: string | null } }
  | { kind: 'linked'; provider: string }
  | { kind: 'error'; error: string; description?: string };

/** Validate and complete the callback described by `params`. Single use. */
export async function completeOAuthCallback(params: URLSearchParams): Promise<OAuthCallbackResult> {
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const verifier = sessionStorage.getItem(VERIFIER_KEY) ?? undefined;
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);

  const state = params.get('state');
  // A callback this tab didn't start (or a replay) — never act on it.
  if (!state || !expectedState || state !== expectedState) {
    return { kind: 'error', error: 'invalidOAuthState' };
  }

  const error = params.get('error');
  if (error) return { kind: 'error', error, description: params.get('error_description') ?? undefined };

  const linked = params.get('linked');
  if (linked) return { kind: 'linked', provider: linked };

  const code = params.get('code');
  if (!code) return { kind: 'error', error: 'oauthAuthenticationFailed' };
  try {
    return { kind: 'login', result: await sdk.oauth.exchange(code, state, verifier) };
  } catch (err) {
    return { kind: 'error', error: 'oauthAuthenticationFailed', description: err instanceof Error ? err.message : undefined };
  }
}
